import { fg } from '@atlaskit/platform-feature-flags/fg';

import { getBscGlobalState, sendPackageOperationalEvent } from '../../../common/utils';
import { isProcessingAllowedByPreferencesMaybeSync } from '../../../common/utils/item-processing';
import { CategoryStorageType, Status, type SetItemResult } from '../../../types';
import { ItemKeyCache } from '../../item-key-cache-service';
import { WebStorageType } from '../types';
import { reportSetItemError } from './report-set-item-error';

const isPromise = <T>(value: unknown): value is Promise<T> =>
	value != null && typeof (value as { then?: unknown }).then === 'function';

// SET COOKIE ANALOGUE
/**
 * Sets a WebStorage item asynchronously. This queries user preferences before setting
 * the item, so it must be used for any items which are not strictly necessary.
 */
export const setStorageItem = async (
	itemKey: string,
	value: string,
	storageType: WebStorageType,
	productName: string | undefined,
	browserStorage: Storage,
): Promise<SetItemResult> => {
	const categoryStorageType =
		storageType === WebStorageType.LocalStorage
			? CategoryStorageType.LocalStorage
			: CategoryStorageType.SessionStorage;
	// `disableWebStorageConsentChecks` skips the consent pipeline (analytics-only),
	// but `__UNSAFE_shouldEnforceKeyForRollout(key, type) === true` re-enables enforcement for this key.
	let disableWebStorageConsentChecks = getBscGlobalState().disableWebStorageConsentChecks ?? false;
	if (fg('jfp-magma-selective-storage-enforcement')) {
		const __UNSAFE_shouldEnforceKeyForRollout =
			getBscGlobalState().__UNSAFE_shouldEnforceKeyForRollout;
		const enforceThisKey =
			__UNSAFE_shouldEnforceKeyForRollout?.(itemKey, categoryStorageType) === true;
		disableWebStorageConsentChecks =
			(getBscGlobalState().disableWebStorageConsentChecks ?? false) && !enforceThisKey;
	}
	if (disableWebStorageConsentChecks) {
		const eventAttributes = {
			itemKey,
			storageType,
			consentChecksDisabled: true,
		};
		try {
			// note: at the time of writing, this doesn't actually do anything because browserStorage.setItem calls the original setItem, not the overridden one.
			// see this thread for more information: https://atlassian.slack.com/archives/C05U6KCDNQJ/p1778585948364219
			ItemKeyCache.markSetByPackage(categoryStorageType, itemKey);
			browserStorage.setItem(itemKey, value);
		} catch (e: any) {
			reportSetItemError({
				message: `${storageType} failed to setItem with consent checks disabled. ${e.message || ''}`,
				attributes: eventAttributes,
				error: e,
			});
			return { status: Status.ERROR, error: e };
		}
		let wasAsync: boolean = true;
		try {
			let shouldSetItem: boolean;
			const shouldSetItemOrPromise = isProcessingAllowedByPreferencesMaybeSync({
				itemKey,
				storageType: categoryStorageType,
				productName,
			});
			if (isPromise(shouldSetItemOrPromise)) {
				shouldSetItem = await shouldSetItemOrPromise;
			} else {
				wasAsync = false;
				shouldSetItem = shouldSetItemOrPromise;
			}

			sendPackageOperationalEvent({
				action: 'usedAtlBrowserStorageSetItem',
				attributes: {
					...eventAttributes,
					wasRejected: !shouldSetItem,
					wasAsync,
				},
			});
		} catch (e: any) {
			reportSetItemError({
				attributes: {
					...eventAttributes,
					wasAsync,
				},
				message: `Failed to emit analytics for setStorageItem with consent checks disabled. ${e.message || ''}`,
				error: e,
			});
		}

		return { status: Status.SUCCESS };
	}
	// user belongs to an org that opted into cookies management and we should respect their preferences
	let shouldSetItem: boolean;
	let wasAsync: boolean = true;
	const shouldSetItemOrPromise = isProcessingAllowedByPreferencesMaybeSync({
		itemKey,
		storageType: categoryStorageType,
		productName,
	});
	if (isPromise(shouldSetItemOrPromise)) {
		shouldSetItem = await shouldSetItemOrPromise;
	} else {
		wasAsync = false;
		shouldSetItem = shouldSetItemOrPromise;
	}
	// The synchronous preference check surfaces no block reason:
	// `isProcessingAllowedByPreferencesMaybeSync` takes no blockedCallback, so we cannot tell
	// "key not registered in the allow-list" from "blocked by user consent" here. The BLOCKED
	// result therefore carries `blockedItemCategory: undefined` ("reason unknown"), which the
	// caller reads as such and answers with a terse "was blocked" warning.

	const eventAttributes = {
		wasRejected: !shouldSetItem,
		itemKey,
		storageType,
		consentChecksDisabled: false,
		wasAsync,
	};
	try {
		sendPackageOperationalEvent({
			action: 'usedAtlBrowserStorageSetItem',
			attributes: eventAttributes,
		});

		if (shouldSetItem) {
			// note: at the time of writing, this doesn't actually do anything because browserStorage.setItem calls the original setItem, not the overridden one.
			// see this thread for more information: https://atlassian.slack.com/archives/C05U6KCDNQJ/p1778585948364219
			ItemKeyCache.markSetByPackage(categoryStorageType, itemKey);
			browserStorage.setItem(itemKey, value);
			return { status: Status.SUCCESS };
		} else {
			return { status: Status.BLOCKED, blockedItemCategory: undefined };
		}
	} catch (e: any) {
		reportSetItemError({
			attributes: eventAttributes,
			message: `Failed to set ${storageType} item. ${e.message || ''}`,
			error: e,
		});
		return { status: Status.ERROR, error: e };
	}
};
